import apiClient from './api';

class PromoCodeService {
  /**
   * Get all coupon codes with optional filters
   * @param {Object} params - Query parameters {is_active, search, ordering, page, page_size}
   * @returns {Promise} - List of coupon codes
   */
  async getPromoCodes(params = {}) {
    try {
      const response = await apiClient.get('/subscriptions/admin/coupon-codes/', { params });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching coupon codes:', error);
      return this.handleError(error);
    }
  }

  /**
   * Create coupon code
   * @param {Object} data - Coupon data
   * {
   *   coupon_code: string,
   *   discount_type: 'percentage' | 'fixed',
   *   discount_value: number,
   *   max_uses: number,
   *   valid_from: string (ISO datetime),
   *   valid_until: string (ISO datetime),
   *   is_active: boolean,
   *   offer_duration_type: 'once' | 'cycle' | 'forever',
   *   offer_duration_cycles: number,
   *   applicable_plan_ids: string[] (UUIDs)
   * }
   * @returns {Promise} - Created coupon code
   */
  async createPromoCode(data) {
    try {
      const response = await apiClient.post('/subscriptions/admin/coupon-codes/', data);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error creating coupon code:', error);
      return this.handleError(error);
    }
  }

  /**
   * Get single coupon code by ID
   * @param {string} id - Coupon Code ID (UUID)
   * @returns {Promise} - Coupon code details
   */
  async getPromoCodeById(id) {
    try {
      const response = await apiClient.get(`/subscriptions/admin/coupon-codes/${id}/`);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error fetching coupon code:', error);
      return this.handleError(error);
    }
  }

  /**
   * Update coupon code (full update)
   * @param {string} id - Coupon Code ID (UUID)
   * @param {Object} data - Complete coupon code data
   * @returns {Promise} - Updated coupon code
   */
  async updatePromoCode(id, data) {
    try {
      const response = await apiClient.put(`/subscriptions/admin/coupon-codes/${id}/`, data);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error updating coupon code:', error);
      return this.handleError(error);
    }
  }

  /**
   * Partial update coupon code
   * @param {string} id - Coupon Code ID (UUID)
   * @param {Object} data - Partial coupon code data
   * @returns {Promise} - Updated coupon code
   */
  async patchPromoCode(id, data) {
    try {
      const response = await apiClient.patch(`/subscriptions/admin/coupon-codes/${id}/`, data);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error patching coupon code:', error);
      return this.handleError(error);
    }
  }

  /**
   * Delete coupon code
   * @param {string} id - Coupon Code ID (UUID)
   * @returns {Promise} - Success status
   */
  async deletePromoCode(id) {
    try {
      await apiClient.delete(`/subscriptions/admin/coupon-codes/${id}/`);

      return {
        success: true,
        message: 'Coupon code deleted successfully',
      };
    } catch (error) {
      console.error('Error deleting coupon code:', error);
      return this.handleError(error);
    }
  }

  /**
   * Toggle active status of coupon code
   * @param {string} id - Coupon Code ID (UUID)
   * @returns {Promise} - Updated coupon code
   */
  async toggleActive(id) {
    try {
      const response = await apiClient.post(`/subscriptions/admin/coupon-codes/${id}/toggle_active/`);

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Error toggling coupon code active status:', error);
      return this.handleError(error);
    }
  }

  /**
   * Handle API errors
   * @param {Error} error - Error object
   * @returns {Object} - Formatted error response
   */
  handleError(error) {
    let message = 'An unexpected error occurred';

    if (error.response) {
      if (error.response.data) {
        // Extract error message from response
        if (typeof error.response.data === 'string') {
          message = error.response.data;
        } else if (error.response.data.detail) {
          message = error.response.data.detail;
        } else if (error.response.data.error) {
          message = error.response.data.error;
        } else {
          // Try to get first error from field errors
          const firstKey = Object.keys(error.response.data)[0];
          if (firstKey && Array.isArray(error.response.data[firstKey])) {
            message = error.response.data[firstKey][0];
          } else if (firstKey && error.response.data[firstKey]) {
            message = `${firstKey}: ${error.response.data[firstKey]}`;
          }
        }
      }

      // Handle specific status codes
      if (error.response.status === 400) {
        // Razorpay sync failed or validation error
        if (error.response.data?.detail) {
          message = error.response.data.detail;
        }
      } else if (error.response.status === 403) {
        message = 'Access denied. Admin privileges required.';
      } else if (error.response.status === 404) {
        message = 'Coupon code not found.';
      }
    } else if (error.request) {
      message = 'Unable to connect to server. Please check your connection.';
    }

    return {
      success: false,
      error: message,
    };
  }
}

export default new PromoCodeService();
